import { useCallback, useEffect, useReducer, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Alert,
  Box,
  Button,
  Flex,
  Heading,
  Input,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import { createApiKey, listApiKeys, revokeApiKey } from '../api';
import {
  getLoginRedirectOptions,
  isRecoverableAuthError,
} from '../utils/auth0Errors';

const SETTINGS_RETURN_TO = '/settings';
const SESSION_REFRESH_MESSAGE =
  'Your login session needs a quick refresh before API keys can be managed.';

const initialState = {
  keys: [],
  status: 'idle',
  error: null,
  needsReauth: false,
};

function keysReducer(state, action) {
  switch (action.type) {
    case 'start':
      return { ...state, status: 'loading', error: null, needsReauth: false };
    case 'success':
      return {
        keys: action.payload,
        status: 'success',
        error: null,
        needsReauth: false,
      };
    case 'error':
      return {
        ...state,
        status: 'error',
        error: action.payload,
        needsReauth: Boolean(action.needsReauth),
      };
    default:
      return state;
  }
}

const formatDate = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

function Settings() {
  const {
    isAuthenticated,
    isLoading: isAuthLoading,
    getAccessTokenSilently,
    getIdTokenClaims,
    loginWithRedirect,
    user,
  } = useAuth0();
  const emailVerified = user?.email_verified === true;
  const [{ keys, status, error, needsReauth }, dispatch] = useReducer(
    keysReducer,
    initialState,
  );
  const [newKeyName, setNewKeyName] = useState('Default');
  const [createdKey, setCreatedKey] = useState(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [reauthPending, setReauthPending] = useState(false);
  const loading = status === 'loading';

  const redirectToLogin = useCallback(async () => {
    setReauthPending(true);
    // prompt=consent forces the Auth0 permission step that silent refresh cannot
    // complete, and getLoginRedirectOptions stashes /settings for the round-trip.
    await loginWithRedirect(
      getLoginRedirectOptions(SETTINGS_RETURN_TO, {
        authorizationParams: { prompt: 'consent' },
      }),
    );
  }, [loginWithRedirect]);

  const refreshKeys = useCallback(async () => {
    if (!isAuthenticated) return;
    dispatch({ type: 'start' });
    try {
      const token = await getAccessTokenSilently();
      const data = await listApiKeys(token);
      dispatch({ type: 'success', payload: data?.keys || [] });
    } catch (err) {
      if (isRecoverableAuthError(err)) {
        dispatch({
          type: 'error',
          payload: SESSION_REFRESH_MESSAGE,
          needsReauth: true,
        });
        return;
      }
      const message = err?.body?.error || err?.message || 'Unable to load API keys.';
      dispatch({ type: 'error', payload: message });
    }
  }, [getAccessTokenSilently, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      refreshKeys();
    }
  }, [isAuthenticated, refreshKeys]);

  const handleCreate = async () => {
    setBusy(true);
    setActionError(null);
    setCreatedKey(null);
    try {
      const token = await getAccessTokenSilently();
      let idToken;
      if (typeof getIdTokenClaims === 'function') {
        try {
          const idClaims = await getIdTokenClaims();
          idToken = idClaims?.__raw;
        } catch {
          idToken = undefined;
        }
      }
      const created = await createApiKey(token, {
        name: newKeyName || 'Default',
        idToken,
      });
      setCreatedKey(created);
      setNewKeyName('Default');
      await refreshKeys();
    } catch (err) {
      if (isRecoverableAuthError(err)) {
        await redirectToLogin();
        return;
      }
      setActionError(err?.body?.error || err?.message || 'Unable to create API key.');
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async (keyId) => {
    setBusy(true);
    setActionError(null);
    try {
      const token = await getAccessTokenSilently();
      await revokeApiKey(token, keyId);
      await refreshKeys();
    } catch (err) {
      if (isRecoverableAuthError(err)) {
        await redirectToLogin();
        return;
      }
      setActionError(err?.body?.error || err?.message || 'Unable to revoke API key.');
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = async () => {
    if (!createdKey?.key) return;
    try {
      await navigator.clipboard.writeText(createdKey.key);
    } catch {
      setActionError('Could not copy to clipboard. Select the key and copy manually.');
    }
  };

  if (isAuthLoading) {
    return (
      <Flex align="center" justify="center" py={12}>
        <Spinner color="teal.500" size="lg" />
      </Flex>
    );
  }

  if (!isAuthenticated) {
    return (
      <Box maxW="720px" mx="auto" py={10} px={4}>
        <Stack gap={4}>
          <Heading size="lg">Settings</Heading>
          <Text color="gray.600">Sign in to manage API keys for the public API and MCP.</Text>
          <Button
            colorPalette="teal"
            alignSelf="flex-start"
            onClick={() => loginWithRedirect(getLoginRedirectOptions(SETTINGS_RETURN_TO))}
          >
            Log in
          </Button>
        </Stack>
      </Box>
    );
  }

  if (reauthPending) {
    return (
      <Box maxW="720px" mx="auto" py={10} px={4}>
        <Stack gap={4} align="flex-start">
          <Heading size="lg">Settings</Heading>
          <Text color="gray.600">Redirecting you to sign in again…</Text>
          <Spinner color="teal.500" />
        </Stack>
      </Box>
    );
  }

  return (
    <Box maxW="860px" mx="auto" py={8} px={{ base: 4, md: 0 }}>
      <Stack gap={8}>
        <Box>
          <Heading size="lg">Settings</Heading>
          <Text color="gray.600" mt={2}>
            Create API keys for the DCF Teacher public API and MCP integrations.
          </Text>
        </Box>

        <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" p={5} bg="white">
          <Stack gap={4}>
            <Heading size="md">API keys</Heading>
            <Text color="gray.600" fontSize="sm">
              Free tier: 100 requests/day for financials, filings, search, and sectors.
              Premium unlocks reverse DCF, charts, DCF defaults, and 8-K summaries.
            </Text>
            <Text color="gray.600" fontSize="sm">
              Send keys as <Text as="span" fontFamily="mono">Authorization: Bearer dcf_...</Text>
            </Text>

            {needsReauth ? (
              <Alert.Root status="info">
                <Alert.Indicator />
                <Alert.Content>
                  <Stack gap={3}>
                    <Text>{SESSION_REFRESH_MESSAGE}</Text>
                    <Text fontSize="sm" color="gray.600">
                      This is a one-time Auth0 permission step for API access on this device.
                    </Text>
                    <Button
                      colorPalette="teal"
                      alignSelf="flex-start"
                      onClick={() => {
                        void redirectToLogin();
                      }}
                    >
                      Sign in again
                    </Button>
                  </Stack>
                </Alert.Content>
              </Alert.Root>
            ) : (
              <Stack gap={3}>
                {!emailVerified && (
                  <Alert.Root status="warning">
                    <Alert.Indicator />
                    <Alert.Content>
                      <Stack gap={3}>
                        <Text>
                          Verify your email before creating API keys. If you just verified in
                          another tab, try Create key anyway — the server re-checks — or refresh
                          your session so this page picks up the update.
                        </Text>
                        <Button
                          colorPalette="teal"
                          variant="outline"
                          alignSelf="flex-start"
                          onClick={() => {
                            void redirectToLogin();
                          }}
                        >
                          Refresh session
                        </Button>
                      </Stack>
                    </Alert.Content>
                  </Alert.Root>
                )}
                <Flex gap={3} direction={{ base: 'column', sm: 'row' }} align={{ sm: 'flex-end' }}>
                  <Box flex="1">
                    <Text fontSize="sm" mb={1} color="gray.700">
                      Key name
                    </Text>
                    <Input
                      value={newKeyName}
                      onChange={(e) => setNewKeyName(e.target.value)}
                      placeholder="Default"
                    />
                  </Box>
                  <Button colorPalette="teal" onClick={handleCreate} loading={busy}>
                    Create key
                  </Button>
                </Flex>
              </Stack>
            )}

            {createdKey?.key && (
              <Alert.Root status="success">
                <Alert.Indicator />
                <Alert.Content>
                  <Stack gap={2}>
                    <Text fontWeight="semibold">Copy your new API key now. It will not be shown again.</Text>
                    <Box
                      fontFamily="mono"
                      fontSize="sm"
                      p={2}
                      bg="green.50"
                      borderRadius="sm"
                      wordBreak="break-all"
                    >
                      {createdKey.key}
                    </Box>
                    <Button size="sm" variant="outline" alignSelf="flex-start" onClick={handleCopy}>
                      Copy key
                    </Button>
                  </Stack>
                </Alert.Content>
              </Alert.Root>
            )}

            {actionError && (
              <Alert.Root status="error">
                <Alert.Indicator />
                <Alert.Content>{actionError}</Alert.Content>
              </Alert.Root>
            )}

            {!needsReauth && error && (
              <Alert.Root status="error">
                <Alert.Indicator />
                <Alert.Content>{error}</Alert.Content>
              </Alert.Root>
            )}

            {needsReauth ? null : loading && keys.length === 0 ? (
              <Flex justify="center" py={8}>
                <Spinner color="teal.500" />
              </Flex>
            ) : keys.length === 0 ? (
              <Text color="gray.500">No API keys yet.</Text>
            ) : (
              <Stack gap={3}>
                {keys.map((key) => (
                  <Flex
                    key={key.id}
                    justify="space-between"
                    align={{ base: 'flex-start', md: 'center' }}
                    direction={{ base: 'column', md: 'row' }}
                    gap={3}
                    borderWidth="1px"
                    borderColor="gray.100"
                    borderRadius="md"
                    p={3}
                  >
                    <Box>
                      <Text fontWeight="medium">{key.name}</Text>
                      <Text fontFamily="mono" fontSize="sm" color="gray.600">
                        {key.key_prefix}…
                      </Text>
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        Created {formatDate(key.created_at)}
                        {key.last_used_at ? ` · Last used ${formatDate(key.last_used_at)}` : ''}
                        {key.revoked_at ? ` · Revoked ${formatDate(key.revoked_at)}` : ' · Active'}
                      </Text>
                    </Box>
                    {key.active && (
                      <Button
                        size="sm"
                        variant="outline"
                        colorPalette="red"
                        onClick={() => handleRevoke(key.id)}
                        disabled={busy}
                      >
                        Revoke
                      </Button>
                    )}
                  </Flex>
                ))}
              </Stack>
            )}
          </Stack>
        </Box>

        <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" p={5} bg="white">
          <Stack gap={2}>
            <Heading size="md">Docs</Heading>
            <Text color="gray.600" fontSize="sm">
              Full guide: <Box as="a" href="/docs" color="teal.700">API &amp; MCP Docs</Box>
            </Text>
            <Text color="gray.600" fontSize="sm">
              OpenAPI: <Text as="span" fontFamily="mono">GET /api/v1/openapi.json</Text>
            </Text>
            <Text color="gray.600" fontSize="sm">
              Example:{' '}
              <Text as="span" fontFamily="mono">
                curl -H &quot;Authorization: Bearer dcf_...&quot; &quot;/api/v1/stocks/AAPL/financials&quot;
              </Text>
            </Text>
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}

export default Settings;
